import { createPinia } from 'pinia';
import { createApp } from 'vue';
// GameState 分片注册清单：必须最早挂载，先于任何读档/建档路径
import './gameStateSlices';
import IsekaiRPGApp from './AppRoot.vue';
import { applyDeviceClass } from '../shared/device';
import { r2Resources } from '../resources/r2Resources';
import { router } from './router';
import { LLMProvider } from '../modules/llm/llmProvider';
import { registerStoryHistoryEventSubscribers } from '../modules/history/historyEventSubscriptions';
import { useNotificationStore } from '../modules/ui/uiStores';
import { LogService } from '../shared/logging/logService';
import { ResourceLoader } from '../shared/resources/resourceLoader';

// 初始化资源加载
const initResources = async () => {
  // 并行加载资源（仅保留欢迎页实际使用的字体和关键图片）
  const resourcePromises = [
    // 预先注入欢迎页与全局 UI 共用字体，尽量避免首屏回退到系统字体
    ResourceLoader.injectFont('AlimamaFangYuanTi', r2Resources.fonts.alimama, {
      weight: '900',
      style: 'normal',
    }),
    // 预加载关键图片（欢迎背景）
    ResourceLoader.loadImage(r2Resources.pictures.welcomeBackground),
  ];

  try {
    await Promise.all(resourcePromises);
    LogService.info('IsekaiRPG', '关键资源已加载并缓存');
  } catch (error) {
    LogService.error('IsekaiRPG', '资源加载出错', error as Error);
  }
};

// 立即开始加载资源
initResources();

const warmupLLMProvider = () => {
  const run = () => {
    void LLMProvider.init()
      .then(() => {
        LogService.info('IsekaiRPG', 'LLM 配置已初始化，等待玩家配置 provider/model/apiKey');
      })
      .catch(error => {
        LogService.error('IsekaiRPG', 'LLM 配置后台初始化失败', error as Error);
      });
  };

  // 首帧先让欢迎页挂出来，再在下一帧预热本地配置读取。
  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(run);
    return;
  }

  window.setTimeout(run, 0);
};

// ============================================
// 启动流程
// ============================================

/**
 * 在 DOM 就绪后执行回调
 */
const onDomReady = (callback: () => void) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
    return;
  }
  callback();
};

onDomReady(async () => {
  // 在应用挂载前设置设备类，避免首次渲染闪动
  applyDeviceClass();
  window.addEventListener('resize', applyDeviceClass);
  window.addEventListener('orientationchange', applyDeviceClass);

  // 创建 Vue 应用
  const app = createApp(IsekaiRPGApp);

  // 注册 Pinia
  const pinia = createPinia();
  app.use(pinia);

  // 注册领域事件订阅（history 主历史投影），需在首个游戏事件产生前完成
  registerStoryHistoryEventSubscribers();

  // 初始化日志服务（必须在 Pinia 注册后），由 app 层桥接 UI Toast，避免 shared 反向依赖 UI 模块。
  const notificationStore = useNotificationStore();
  LogService.init(notification => {
    notificationStore.error(notification.displayMessage, notification.title);
  });

  // 全局错误处理
  app.config.errorHandler = (err, _instance, info) => {
    LogService.error('Vue', `全局错误: ${info}`, err as Error);
  };

  window.onerror = (message, _source, _lineno, _colno, error) => {
    LogService.error('Window', `未捕获错误: ${message}`, error);
    return true; // 阻止默认处理
  };

  window.onunhandledrejection = event => {
    LogService.error('Promise', '未处理的 Promise 拒绝', event.reason);
  };

  // 注册路由
  app.use(router);

  // 挂载到 #app 元素
  app.mount('#app');

  warmupLLMProvider();
});
